import React, {ReactElement, ReactNode} from 'react';

import {useTranslation} from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';

import {ReactComponent as ExportIcon} from 'assets/svg/download.svg';
import {Icon} from 'components/ui-kit/Icon';
import {Spinner} from 'components/ui-kit/Spinner';
import {colors} from 'theme';
import {formatNumber} from 'utils/number';

import {
    EndMessage,
    ExportButton,
    HeaderControls,
    ListHeader,
    Placeholder,
    SpinnerLayer,
    TotalRowsCount,
} from './styled';

interface Props<T> {
    rows?: T[];
    hasMore?: boolean;
    totalRowsCount?: number;
    headerControls?: ReactNode;
    renderRow: (row: T) => ReactNode;
    onNextPage?: () => void;
    className?: string;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    configurationBlock?: ReactElement;
}

export const EndlessList: <T>(p: Props<T>) => React.ReactElement = ({
    rows = [],
    totalRowsCount,
    hasMore,
    headerControls,
    renderRow,
    onNextPage,
    className,
    exportConfig,
    configurationBlock,
}) => {
    const {t} = useTranslation();

    const innerHasMore =
        (totalRowsCount !== undefined && rows.length < totalRowsCount) ||
        (hasMore !== undefined && hasMore);

    return (
        <div className={className}>
            {headerControls && <HeaderControls>{headerControls}</HeaderControls>}

            {configurationBlock}

            {(totalRowsCount || exportConfig) && (
                <ListHeader alignRight={totalRowsCount == null}>
                    {totalRowsCount != null && (
                        <TotalRowsCount>
                            {t('components.endlessList.totalRecords')}:{' '}
                            {formatNumber(totalRowsCount)}
                        </TotalRowsCount>
                    )}
                    {exportConfig != null && (
                        <ExportButton
                            onClick={() => exportConfig.callback()}
                            disabled={exportConfig.exportPending}
                        >
                            <Icon
                                source={ExportIcon}
                                width={20}
                                height={20}
                                fillColor={colors.brandPrimary}
                            />
                            {totalRowsCount == null && t('components.endlessList.export')}
                        </ExportButton>
                    )}
                </ListHeader>
            )}

            <InfiniteScroll
                dataLength={rows.length}
                next={onNextPage || (() => {})}
                hasMore={innerHasMore}
                loader={
                    <SpinnerLayer>
                        <Spinner />
                    </SpinnerLayer>
                }
                endMessage={
                    rows.length > 0 && (
                        <EndMessage>{t('components.endlessList.endMessage')}</EndMessage>
                    )
                }
            >
                {rows.length > 0 &&
                    rows.map((elem, index) => (
                        <React.Fragment key={index}>{renderRow(elem)}</React.Fragment>
                    ))}
                {rows.length === 0 && (
                    <Placeholder>{t('components.endlessList.noData')}</Placeholder>
                )}
            </InfiniteScroll>
        </div>
    );
};
